import { useMutation } from "@apollo/client";
import { noop } from "lodash";
import { useCallback, useContext, useMemo } from "react";
import {
  MoveUserToCalibrationGroupMutation,
  MoveUserToCalibrationGroupMutationVariables,
} from "types/graphql-schema";

import PerformanceAssessmentReportContext from "@apps/reporting/context/performance-assessment-report-context";
import moveUserToCalibrationGroupMutation from "@apps/reporting/graphql/move-user-to-calibration-group-mutation";
import { successNotificationVar } from "@cache/cache";
import {
  DropdownOptionButtonType,
  DropdownOptionSubMenuType,
} from "@components/dropdown/dropdown-types";

import { UserCalibrationGroups } from "./helpers";

/**
 * The row menu's "Move to Group..." on the Calibration tab, for admins: it
 * moves the person of the row to another group, or to "No group", at once.
 * A group the person approves or calibrates is disabled: one role per group,
 * the rule the server checks. Null when the row must not show it.
 */
const useMoveToCalibrationGroupOption = ({
  user,
  userCalibrationGroups,
  skip,
}: {
  user: { id: number; name: string };
  /** From `getCalibrationGroupsByUserId`, read once for the whole table. */
  userCalibrationGroups?: UserCalibrationGroups;
  skip: boolean;
}): DropdownOptionSubMenuType | null => {
  const { complianceProgramId, calibrationGroups } = useContext(
    PerformanceAssessmentReportContext,
  );
  const [moveUserToCalibrationGroup, { loading }] = useMutation<
    MoveUserToCalibrationGroupMutation,
    MoveUserToCalibrationGroupMutationVariables
  >(moveUserToCalibrationGroupMutation);

  const move = useCallback(
    (groupId: number | null, groupName: string) =>
      moveUserToCalibrationGroup({
        variables: {
          complianceProgramId,
          userId: user.id,
          calibrationGroupId: groupId,
        },
        // The table, the grid, the groups list and "Not in a group" read them.
        refetchQueries: ["getComplianceProgramCalibrationGroups"],
        awaitRefetchQueries: true,
        onCompleted: () =>
          successNotificationVar({
            title:
              groupId === null
                ? `${user.name} is no longer in a group`
                : `${user.name} moved to ${groupName}`,
          }),
        // The error link already shows the server's message.
        onError: noop,
      }),
    [complianceProgramId, moveUserToCalibrationGroup, user],
  );

  return useMemo(() => {
    if (skip) {
      return null;
    }
    const currentGroupId = userCalibrationGroups?.group?.id ?? null;
    const option = (
      groupId: number | null,
      groupName: string,
      reason?: string,
    ): DropdownOptionButtonType => {
      const disabledReason =
        groupId === currentGroupId ? "Current group" : reason;
      return {
        type: "button",
        label: groupName,
        description: disabledReason,
        disabled: !!disabledReason,
        onClick: () => move(groupId, groupName),
      };
    };
    return {
      type: "submenu",
      label: "Move to Group...",
      disabled: loading,
      options: [
        ...calibrationGroups.data.map((group) =>
          option(
            group.id,
            group.name,
            userCalibrationGroups?.approverGroupIds.has(group.id)
              ? "Approves this group"
              : userCalibrationGroups?.managerGroupIds.has(group.id)
                ? "On this group's Calibration Team"
                : undefined,
          ),
        ),
        option(null, "No group"),
      ],
    };
  }, [skip, userCalibrationGroups, calibrationGroups.data, loading, move]);
};

export default useMoveToCalibrationGroupOption;
